import React, {
  useState,
  useEffect,
} from 'react'
import {
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useMutation, useQuery } from "@tanstack/react-query"
import axios from "axios"
import "react-tooltip/dist/react-tooltip.css"
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import {
  ROUTE_PATHS,
} from '../library/helper'

import NotFound from '../components/NotFound'
import LoadingCard from '../components/LoadingCard'

const useClientProductSubmission = () => {
  return useMutation({
    mutationKey: [
      'client-product-submission',
    ],
    retry: false,
    mutationFn: async (data) => {
      return axios({
        url: [
          process.env.REACT_APP_BASE_SERVER,
          '/client-product-submission',
        ].join(''),
        method: 'POST',
        withCredentials: true,
        data,
      })
    },
  })
}

const useClientProductLookup = ({
  id,
}) => {
  return useQuery({
    queryKey: [
      'client-product-lookup',
      id,
    ],
    retry: false,
    enabled: !!id,
    queryFn: ({ signal }) => axios({
      url: [
        process.env.REACT_APP_BASE_SERVER,
        '/client-product-lookup',
      ].join(''),
      method: 'GET',
      withCredentials: true,
      signal,
      params: {
        id,
      },
    }).then(response => {
      return response?.data?.data ?? {}
    }),
  })
}

export default function SettingRevenueAndProductionForm({onSuccess, isDialogModal, id: idProp}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { id: idParam } = useParams()
  const id = idProp ?? idParam

  const query = useClientProductLookup({
    id,
  })

  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [type, setType] = useState('');
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    if (query.data) {
      setName(query.data.product?.name || '')
      setUnit(query.data.product?.unit || '')
      setType(query.data.product?.type || '')
      setIsDefault(query.data.product?.is_default)
    }
  }, [query.data])

  const {
    mutate,
    isPending,
  } = useClientProductSubmission()

  const onSaveRecord = async (e) => {
    mutate({
      name,
      unit,
      isDefault,
      id,
      type,
    }, {
      onSuccess: () => {
        Swal.fire({
          icon: 'success',
          title: '',
          html: '<p>Successfully record settings</p>',
          showCancelButton: false,
          confirmButtonColor: '#0A564B',
          confirmButtonText: 'Ok'
        }).then((result) => {
          if (result.isConfirmed) {
            if (typeof onSuccess === 'function') {
              onSuccess()
            } else {
              navigate(ROUTE_PATHS.settingRevenue)
            }
          }
        })
      },
      onError: (error) => {
        let message
        if (typeof error.response?.data?.message === 'string') {
          message = error.response.data.message
        } else if (error.message) {
          message = error.message
        }
        Swal.fire({
          title: '',
          html: `<p>${message}</p>`,
          showCancelButton: false,
          confirmButtonColor: '#0A564B',
          confirmButtonText: 'Ok',
          icon: 'error',
        })
      }
    })
  }

  const onInputChange = (e) => {
    const { name, value } = e.target
    if (name === 'name') {
      setName(value)
    } else if (name === 'unit') {
      setUnit(value)
    } else if (name === 'type') {
      setUnit('')
      setType(value)
      if (value === 'revenue') {
        setUnit((u) => u || 'IDR') // default to IDR if empty
      } else {
        setUnit('Ton')
      }
    }
  }

  const onSetAsDefault = () => {
    setIsDefault(prev => !prev)
  }

  return (
      <div className={`page-content ${isDialogModal ? "m-0 p-0" : ""}`}>
      <div className="container-fluid">

        {isDialogModal ?? <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">
                {t("menu.label22")}
              </h4>
            </div>
          </div>
        </div>}

        <div className="row">
          <div className="col-lg-12">

            <div className="card">
              {isDialogModal ??<div className="card-header align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">
                  {id ? t('general.btnUpdate') : t('general.btnCreate')}
                </h4>
              </div>}

              {
                isPending || query.isFetching ? (
                  <div className="card-body">
                    <LoadingCard />
                  </div>
                ) : <>
                  {
                    query.failureReason?.response?.data?.status === 404 ? (
                      <div className="card-body">
                        <NotFound />
                      </div>
                    ) : (
                      <div id="create-unit-box" className="card-body">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label d-block">
                                {t('setting.revenueAndProduction.type')} <span className="text-danger">*</span>
                              </label>

                              <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="type"
                                    id="typeRevenue"
                                    value="revenue"
                                    checked={type === "revenue"}
                                    onChange={onInputChange}
                                />
                                <label className="form-check-label" htmlFor="typeRevenue">
                                  Revenue
                                </label>
                              </div>

                              <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="type"
                                    id="typeProduction"
                                    value="production"
                                    checked={type === "production"}
                                    onChange={onInputChange}
                                />
                                <label className="form-check-label" htmlFor="typeProduction">
                                  Production
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label htmlFor="name" className="form-label">
                                {t('setting.revenueAndProduction.label')} <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                id="name"
                                name="name"
                                onChange={onInputChange}
                                value={name}
                                className="form-control"
                                placeholder={t('setting.revenueAndProduction.form.enter')}
                              >
                              </input>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label htmlFor="unit" className="form-label">
                                {t('setting.revenueAndProduction.unit')} <span className="text-danger">*</span>
                              </label>

                              {type === 'revenue' ? (
                                  <select
                                      id="unit"
                                      name="unit"
                                      className="form-select"
                                      value={unit}
                                      onChange={onInputChange}
                                  >
                                    <option value="IDR">IDR</option>
                                    <option value="USD">USD</option>
                                  </select>
                              ) : (
                                  <input
                                      type="text"
                                      id="unit"
                                      name="unit"
                                      className="form-control"
                                      value={unit || 'Ton'}
                                      readOnly
                                  />
                              )}
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className="col-md-6">
                            <div className="form-check form-switch form-switch-custom form-switch-primary mb-3 mt-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="makeDefault"
                                checked={isDefault}
                                onChange={onSetAsDefault}
                              >
                              </input>
                              <label
                                className="form-check-label"
                                htmlFor="makeDefault"
                                style={{
                                  color: isDefault ? 'inherit' : '#878a99',
                                }}
                              >
                                {t('setting.revenueAndProduction.form.makeDefault')}
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="text-end">
                            <button
                              onClick={onSaveRecord}
                              type="submit"
                              className="btn btn-primary"
                            >
                              {t('general.btnSave')} {isPending ? '...' : ''}
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  }
                </>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
